import React, { useEffect, useRef, useState } from "react";
import { Cropper } from "react-cropper";
import Loader from "../../common/components/Loader";
import "cropperjs/dist/cropper.css";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadingOverlay from "react-loading-overlay";
import { useDispatch, useSelector } from "react-redux";
import CopyToClipboard from "react-copy-to-clipboard";
import { showAlert } from "../../../config/utils";
import noDataForAnalysis from "../../../assets/img/no-data-tr.svg";
import actions from "../actions";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Portal from "../../common/components/Portal";
import commonActions from "../../common/actions";

const CropperModal = ({ selectedImg, onCancel, analyseClick, isSurveyAssetImage, selectedImgId, analyseKey, isAnalysed, setIsAnalysed }) => {
    const cropperRef = useRef(null);
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const initalZoomLevelRef = useRef(null);
    const [zoomLevel, setZoomLevel] = useState(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [isImageLoading, setIsImageLoading] = useState(true);
    const [analysedData, setAnalysedData] = useState({ text: [], labels: [] });
    const [displayData, setDisplayData] = useState([]);
    const [copySuccessIndex, setCopySuccessIndex] = useState(false);
    const [editableItem, setEditableItem] = useState(false);
    const [editedText, setEditedText] = useState("");
    const [showErrorBorder, setShowErrorBorder] = useState(false);
    const [chosenData, setChosenData] = useState({ image_text: [], image_labels: [] });
    const [search, setSearch] = useState("");
    const { getRawImageAnalysisData, savedRawImageAnalysisData } = useSelector(state => state.imageReducer);
    const currentTab = analyseKey === "text" ? "image_text" : "image_labels";
    const [imageSrc, setImageSrc] = useState("");
    const [showClearConfirmationModal, setClearConfirmationModal] = useState(false);
    const [analyseAgain, setAnalyseAgain] = useState(false);

    useEffect(() => {
        if (getRawImageAnalysisData?.success && croppedImageUrl) {
            const imageData = getRawImageAnalysisData?.image_text?.[selectedImgId] || { text: [], labels: [] };
            const { text = [], labels = [] } = imageData;
            setCroppedImageUrl(null);
            setAnalysedData({ text, labels });
            setDisplayData({ text, labels });
            setChosenData({ image_text: text, image_labels: labels });
        } else {
            const defaultData = { text: [], labels: [] };
            setAnalysedData(defaultData);
            setDisplayData(defaultData);
            setChosenData({ image_text: [], image_labels: [] });
        }
    }, [getRawImageAnalysisData]);

    useEffect(() => {
        if (isAnalysed && croppedImageUrl) {
            const objectUrl = URL.createObjectURL(croppedImageUrl);
            setImageSrc(objectUrl);

            return () => {
                URL.revokeObjectURL(objectUrl);
            };
        } else {
            setImageSrc(selectedImg);
        }
    }, [isAnalysed, selectedImg]);

    useEffect(() => {
        if (chosenData?.image_text?.length || chosenData?.image_labels?.length) {
            if (savedRawImageAnalysisData?.success) {
                showAlert("Saved Successfully");
                setCroppedImageUrl(null);
                onCancel();
            } else {
                showAlert("Saved Successfully");
            }
        }
    }, [savedRawImageAnalysisData]);
    const onCrop = event => {
        const cropper = cropperRef.current?.cropper;
        if (cropper && cropper?.canvasData?.minWidth) {
            const canvas = cropper.getCroppedCanvas();

            if (canvas) {
                const compressImage = async (quality = 0.7) => {
                    const sizeLimit = 10 * 1024 * 1024;

                    const generateBlob = q => new Promise(resolve => canvas.toBlob(blob => resolve(blob), "image/jpeg", q));

                    const blob = await generateBlob(quality);

                    if (blob && blob.size <= sizeLimit) {
                        setCroppedImageUrl(blob);
                    } else if (quality > 0.1) {
                        compressImage(quality - 0.1);
                    }
                };

                compressImage();
            }
        }
    };

    const onReady = () => {
        const cropper = cropperRef.current?.cropper;
        setIsImageLoading(false);
        const image = cropper?.image;
        const container = cropper?.getContainerData();

        if (image && container) {
            const imageWidth = image.naturalWidth;
            const imageHeight = image.naturalHeight;
            const containerWidth = container.width;
            const containerHeight = container.height;

            const scaleX = containerWidth / imageWidth;
            const scaleY = containerHeight / imageHeight;
            const zoom = Math.min(scaleX, scaleY);

            cropper?.zoomTo(zoom);
            initalZoomLevelRef.current = zoom;
        }
    };

    const handleZoom = event => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;
        let zoomValue = initalZoomLevelRef?.current;
        if (event.detail.ratio > 2) {
            event.preventDefault();
            cropper.zoomTo(2);
            zoomValue = 2;
            return;
        }
        if (event.detail.ratio < initalZoomLevelRef?.current) {
            event.preventDefault();
            zoomValue = initalZoomLevelRef?.current;
            cropper.zoomTo(initalZoomLevelRef?.current);
            return;
        }
        if (event.detail.ratio < 2 && event?.detail?.ratio > initalZoomLevelRef?.current) zoomValue = event?.detail?.ratio;
        setZoomLevel(zoomValue);
        onCrop(event);
    };

    const zoomIn = () => {
        if (cropperRef.current) {
            const cropper = cropperRef.current.cropper;
            const newZoomLevel = Math.min(zoomLevel + 0.1, 2);
            setZoomLevel(newZoomLevel);
            cropper.zoomTo(newZoomLevel);
            onCrop();
        }
    };
    const zoomOut = () => {
        if (cropperRef.current) {
            const cropper = cropperRef.current.cropper;
            const newZoomLevel = Math.max(zoomLevel - 0.1, initalZoomLevelRef?.current);
            setZoomLevel(newZoomLevel);
            cropper.zoomTo(newZoomLevel);
            onCrop();
        }
    };
    const resetCrop = () => {
        const cropper = cropperRef.current?.cropper;
        if (cropper) {
            cropper.reset();
            cropper?.clear();
            setZoomLevel(initalZoomLevelRef?.current);
            setCroppedImageUrl(null);
            const image = cropper?.image;
            const container = cropper?.getContainerData();

            if (image && container) {
                const imageWidth = image.naturalWidth;
                const imageHeight = image.naturalHeight;
                const containerWidth = container.width;
                const containerHeight = container.height;

                const scaleX = containerWidth / imageWidth;
                const scaleY = containerHeight / imageHeight;
                const zoom = Math.min(scaleX, scaleY);

                cropper?.zoomTo(zoom);
                initalZoomLevelRef.current = zoom;
            }
        }
    };

    const handleUpdateChanges = () => {
        const isEditedTextDuplicate = analysedData?.[analyseKey]?.some(
            (item, index) => index !== editableItem && item.toLowerCase() === editedText?.toLowerCase()
        );

        if (isEditedTextDuplicate) {
            setShowErrorBorder(true);
            showAlert("Already exists");
            return;
        }

        const updateTabData = (data, tab) => {
            const updatedData = { ...data };
            const currentTabData = [...updatedData?.[tab]];
            const itemIndex = currentTabData.findIndex(item => item === editableItem);
            if (itemIndex !== -1) {
                currentTabData[itemIndex] = editedText;
            }
            updatedData[tab] = currentTabData;
            return updatedData;
        };

        setShowErrorBorder(false);

        setAnalysedData(prevData => updateTabData(prevData, analyseKey));
        setDisplayData(prevData => updateTabData(prevData, analyseKey));
        setChosenData(prevData => {
            const tabKey = analyseKey === "text" ? "image_text" : "image_labels";
            return updateTabData(prevData, tabKey);
        });

        showAlert("Image analyze data updated");
        setEditedText("");
        setEditableItem("");
    };

    const handleAnalysisCheck = (name, label) => {
        setChosenData(prevData => {
            const isNameExists = prevData[currentTab]?.includes(name);
            if (isNameExists) {
                return {
                    ...prevData,
                    [currentTab]: prevData[currentTab].filter(item => item !== name)
                };
            } else {
                return {
                    ...prevData,
                    [currentTab]: [...prevData[currentTab], name]
                };
            }
        });
    };
    const handleSelectAll = checked => {
        setChosenData(prevData => {
            const itemsToSelect = analysedData[analyseKey] || [];

            return {
                ...prevData,
                [currentTab]: checked ? itemsToSelect : []
            };
        });
    };
    const handleSaveAnalysedData = () => {
        dispatch(
            actions?.addAnalysedData({
                image_id: selectedImgId,
                img_type: isSurveyAssetImage ? "survey_image_upload" : analyseKey,
                type: isSurveyAssetImage ? "survey_image_upload" : "image_upload",
                ...chosenData
            })
        );
    };
    const handleSearch = event => {
        let textResults = analysedData?.[analyseKey];
        if (event) {
            textResults = analysedData?.[analyseKey].filter(item => item.toLowerCase().includes(search.toLowerCase()));
        }
        setDisplayData({ ...displayData, [analyseKey]: textResults });
    };
    const renderClearConfirmationModal = () => {
        if (!showClearConfirmationModal && !analyseAgain) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type={analyseAgain ? "" : "cancel"}
                        heading={
                            analyseAgain
                                ? "Do you want to analyze again?"
                                : showClearConfirmationModal === "cancel"
                                ? "Do you want to clear and lose all changes?"
                                : `Are you sure you want to clear the analyzed data?`
                        }
                        message={
                            analyseAgain
                                ? "This will overwrite the current analyzed data."
                                : showClearConfirmationModal === "cancel"
                                ? "This action cannot be reverted, are you sure that you need to cancel?"
                                : ""
                        }
                        onNo={() => {
                            setClearConfirmationModal(false);
                            setAnalyseAgain(false);
                        }}
                        onYes={async () => {
                            if (analyseAgain) {
                                setAnalyseAgain(false);
                                analyseClick(croppedImageUrl, true);
                            } else if (showClearConfirmationModal === "cancel") {
                                onCancel();
                            } else {
                                await setImageSrc(selectedImg);
                                setIsAnalysed(false);
                                resetCrop();
                                setChosenData({ image_text: [], image_labels: [] });
                                setAnalysedData({ text: [], labels: [] });
                                setDisplayData({ [analyseKey]: [] });
                            }
                            setClearConfirmationModal(false);
                        }}
                    />
                }
                onCancel={() => {
                    setClearConfirmationModal(false);
                }}
            />
        );
    };
    const isAllSelected = chosenData[analyseKey == "text" ? "image_text" : "image_labels"]?.length === analysedData?.[analyseKey]?.length;
    return (
        <div
            id="modalId"
            className="modal modal-region modal-img-magamnt modal-editable-view modal-image-crop"
            style={{ display: "block", cursor: "move" }}
        >
            <Draggable cancel=".not-dragabble">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-dismiss="modal"
                            aria-label="Close"
                            onClick={() => {
                                if (displayData?.[analyseKey]?.length) {
                                    setClearConfirmationModal("cancel");
                                } else {
                                    onCancel();
                                }
                            }}
                        >
                            <span aria-hidden="true">
                                <img src="/img/close.svg" alt="" />
                            </span>
                        </button>
                        <div className="modal-header">
                            <div class="heading position-relative">
                                <h5 className="not-dragabble">{`Image Crop (${analyseKey === "text" ? "Text" : "Object"})`}</h5>
                            </div>
                        </div>
                        <div className="modal-body region-otr img-crop not-dragabble">
                            <div className="d-flex outer-wrap-crop">
                                <div>
                                    <div className="outer-cropper">
                                        {isImageLoading ? (
                                            <Loader />
                                        ) : (
                                            <div className="md-grp-btn">
                                                <button type="button" data-tip="Reset" data-effect="solid" onClick={resetCrop}>
                                                    <i class="fas fa-sync image-icons" style={{ color: "#3b94f4" }}></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={zoomIn}
                                                    disabled={zoomLevel >= 1.9}
                                                    data-tip="Zoom In"
                                                    data-effect="solid"
                                                >
                                                    <img src="/img/zoom-in.svg" alt="" />
                                                </button>
                                                <button
                                                    className={`${
                                                        zoomLevel <= initalZoomLevelRef?.current || !initalZoomLevelRef?.current
                                                            ? "cursor-disabled"
                                                            : ""
                                                    }`}
                                                    type="button"
                                                    onClick={zoomOut}
                                                    disabled={zoomLevel <= initalZoomLevelRef?.current || !initalZoomLevelRef?.current}
                                                    data-tip="Zoom out"
                                                    data-effect="solid"
                                                >
                                                    <img src="/img/zoom-out.svg" alt="" />
                                                </button>
                                                <ReactTooltip
                                                    place="top"
                                                    className="rc-tooltip-custom-class"
                                                    effect="solid"
                                                    backgroundColor="#007bff"
                                                />
                                            </div>
                                        )}
                                        <Cropper
                                            style={{ width: "500px", height: "580px ", background: "#ececec" }}
                                            ref={cropperRef}
                                            initialAspectRatio={1}
                                            preview=".img-preview"
                                            src={imageSrc}
                                            viewMode={1}
                                            minCropBoxHeight={10}
                                            minCropBoxWidth={10}
                                            background={false}
                                            responsive={true}
                                            autoCropArea={1}
                                            checkOrientation={false}
                                            guides={true}
                                            crossOrigin="anonymous"
                                            checkCrossOrigin={false}
                                            cropend={onCrop}
                                            autoCrop={false}
                                            className="not-dragabble"
                                            ready={onReady}
                                            zoomTo={0.6}
                                            zoom={handleZoom}
                                            toggleDragModeOnDblclick={false}
                                        />
                                    </div>
                                    {!isAnalysed && (
                                        <div className="btnOtr crop-anl-btn">
                                            <button
                                                type="button"
                                                className={`mr-2 ml-2 btn btn-secondary btnClr`}
                                                onClick={() => {
                                                    onCancel();
                                                }}
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                type="button"
                                                className={`mr-0 btn btn-primary not-draggable ${!croppedImageUrl ? "cursor-disabled" : ""}`}
                                                onClick={() => {
                                                    analyseClick(croppedImageUrl);
                                                }}
                                                disabled={!croppedImageUrl}
                                            >
                                                <img src="/img/analyze-icon.svg" alt="" />
                                                Analyze
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {isAnalysed && (
                                    <div className="cnt-img-se outer-right-tabpane not-dragabble">
                                        <LoadingOverlay active={getRawImageAnalysisData?.isLoading} spinner={<Loader />} fadeSpeed={10}>
                                            <div className="img-itms meta not-dragabble">
                                                <div className="img-txt-label-outer">
                                                    <ul className="list-sec-buton">
                                                        <div class="d-flex justify-content-end w-100 btn-tx-obj">
                                                            <button
                                                                type="button"
                                                                className={`mr-2 btn btn-secondary btnClr`}
                                                                onClick={() => {
                                                                    if (analysedData?.[analyseKey]?.length) {
                                                                        setClearConfirmationModal(true);
                                                                    } else {
                                                                        setImageSrc(selectedImg);
                                                                        setTimeout(() => {
                                                                            resetCrop();
                                                                        }, 300);
                                                                    }
                                                                }}
                                                                // disabled={
                                                                //     (!initalZoomLevelRef?.current || zoomLevel <= initalZoomLevelRef?.current) &&
                                                                //     !croppedImageUrl &&
                                                                //     !analysedData[analyseKey]?.length
                                                                // }
                                                            >
                                                                <img src="/img/clear_icon.svg" alt="" />
                                                                Clear
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`mr-0 btn btn-primary not-draggable ${
                                                                    !croppedImageUrl ? "cursor-disabled" : ""
                                                                }`}
                                                                onClick={() => {
                                                                    setAnalyseAgain(true);
                                                                    // setClearConfirmationModal("analyze_again");
                                                                }}
                                                                disabled={!croppedImageUrl}
                                                            >
                                                                <img src="/img/analyze-icon.svg" alt="" />
                                                                Analyze Again
                                                            </button>
                                                        </div>
                                                    </ul>
                                                    <div className="search-editable-pop">
                                                        <input
                                                            className="search-editable-pop"
                                                            type="text"
                                                            placeholder="Search..."
                                                            value={search}
                                                            onChange={event => {
                                                                if (event?.target?.value === "") handleSearch();
                                                                setSearch(event.target.value);
                                                            }}
                                                            onKeyDown={event => {
                                                                if (event.key === "Enter") {
                                                                    handleSearch(event);
                                                                }
                                                            }}
                                                        />
                                                        <span
                                                            class="close-sr"
                                                            onClick={() => {
                                                                setEditableItem(false);
                                                                setSearch("");
                                                                handleSearch();
                                                            }}
                                                        >
                                                            <i class="fa fa-times"></i>
                                                        </span>
                                                    </div>
                                                    <ul>
                                                        <div
                                                            className={`list-detail pt-0  ${
                                                                displayData?.[analyseKey]?.length === 0 ? "no-data" : ""
                                                            }`}
                                                        >
                                                            {displayData?.[analyseKey]?.length > 0 ? (
                                                                <>
                                                                    <label class="container-check mt-3">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isAllSelected}
                                                                            onChange={e => handleSelectAll(e.target.checked)}
                                                                            className="form-control not-draggable"
                                                                        />
                                                                        <span class="checkmark"></span> Select All
                                                                    </label>
                                                                    {displayData?.[analyseKey]?.map((item, i) => (
                                                                        <>
                                                                            <li
                                                                                className={`${editableItem === item ? "input-section-band" : ""} ${
                                                                                    i === 0 && "mt-2"
                                                                                } ${showErrorBorder && editableItem === item ? "error-border " : ""}`}
                                                                                key={i}
                                                                            >
                                                                                {editableItem === item ? (
                                                                                    <div ref={inputRef} className="analyse-field">
                                                                                        <input
                                                                                            type="text"
                                                                                            value={editedText}
                                                                                            onChange={e => {
                                                                                                setEditedText(e?.target?.value);
                                                                                            }}
                                                                                            autoFocus
                                                                                            onKeyDown={e => {
                                                                                                if (e.key === "Enter") {
                                                                                                    handleUpdateChanges(analyseKey, "save");
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                        <div className="btn-icons-out">
                                                                                            <button
                                                                                                type="button"
                                                                                                className={`btn-save-tick ${
                                                                                                    editedText === item ? "cursor-disabled" : ""
                                                                                                }`}
                                                                                                onClick={() =>
                                                                                                    handleUpdateChanges(analyseKey, "save")
                                                                                                }
                                                                                                disabled={editedText === item}
                                                                                            >
                                                                                                <i class="fa fa-check" currentitem="false"></i>
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className={`btn-save-close`}
                                                                                                onClick={() => {
                                                                                                    setEditableItem(false);
                                                                                                    setEditedText("");
                                                                                                }}
                                                                                            >
                                                                                                <i class="fa fa-times" currentitem="false"></i>
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="list-band">
                                                                                        <label class="container-check mt-3">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={chosenData?.[currentTab]?.includes(item)}
                                                                                                onChange={e => handleAnalysisCheck(item)}
                                                                                                className="form-control not-draggable"
                                                                                            />
                                                                                            <span class="checkmark"></span> {item}
                                                                                        </label>
                                                                                        <div>
                                                                                            <div
                                                                                                class={`icon mr-2 cursor-hand`}
                                                                                                onClick={() => {
                                                                                                    setEditableItem(item);
                                                                                                    setEditedText(item);
                                                                                                }}
                                                                                            >
                                                                                                <i
                                                                                                    class="fa fa-pencil-alt"
                                                                                                    data-tip="Edit"
                                                                                                    data-for="analyse"
                                                                                                    data-effect="solid"
                                                                                                ></i>
                                                                                            </div>
                                                                                            <CopyToClipboard
                                                                                                text={item}
                                                                                                onCopy={() => {
                                                                                                    setCopySuccessIndex(i);
                                                                                                    setTimeout(() => {
                                                                                                        setCopySuccessIndex(false);
                                                                                                    }, 3500);
                                                                                                }}
                                                                                            >
                                                                                                <div class="icon cursor-hand">
                                                                                                    {copySuccessIndex === i ? (
                                                                                                        <i
                                                                                                            class="fa fa-check"
                                                                                                            style={{ color: "green" }}
                                                                                                            data-tip={"Copied to Clipboard"}
                                                                                                            data-effect="solid"
                                                                                                            data-for="analyse"
                                                                                                            data-place="left"
                                                                                                        ></i>
                                                                                                    ) : (
                                                                                                        <i
                                                                                                            class="fa fa-copy"
                                                                                                            onClick={() =>
                                                                                                                showAlert("Copied to Clipboard")
                                                                                                            }
                                                                                                            data-tip={"Click to Copy"}
                                                                                                            data-effect="solid"
                                                                                                            data-for="analyse"
                                                                                                            data-place="left"
                                                                                                        ></i>
                                                                                                    )}
                                                                                                </div>
                                                                                            </CopyToClipboard>
                                                                                            <ReactTooltip
                                                                                                clickable={false}
                                                                                                id="analyse"
                                                                                                effect="solid"
                                                                                                place="top"
                                                                                                className="rc-tooltip-custom-class"
                                                                                                backgroundColor="#111314"
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </li>
                                                                        </>
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <div className="no-data-outer">
                                                                    <img src={noDataForAnalysis} alt="No data for analysis" />
                                                                    <h3>No data Found</h3>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </ul>
                                                </div>
                                                <div className="btnOtr crop-anl-btn">
                                                    <button
                                                        type="button"
                                                        className={`mr-2 ml-2 btn btn-secondary btnClr`}
                                                        onClick={() => {
                                                            if (displayData?.[analyseKey]?.length) {
                                                                setClearConfirmationModal("cancel");
                                                            } else {
                                                                onCancel();
                                                            }
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`mr-0 ml-2 btn btn-primary not-draggable ${
                                                            !chosenData?.image_labels?.length && !chosenData?.image_text?.length
                                                                ? "cursor-disabled"
                                                                : ""
                                                        } `}
                                                        onClick={() => {
                                                            handleSaveAnalysedData();
                                                        }}
                                                        disabled={!chosenData?.image_labels?.length && !chosenData?.image_text?.length}
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            </div>
                                        </LoadingOverlay>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
            {renderClearConfirmationModal()}
        </div>
    );
};
export default CropperModal;
